import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  signal,
} from '@angular/core';

export interface NavItem {
  id: string;
  label: string;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
}

/** A collapsible sidebar section whose leaf links switch the page's view when clicked. */
export interface NavPanel {
  id: string;
  label: string;
  groups: NavGroup[];
}

/** Distance from the top of the scroll container at which a section counts as "current". */
const ACTIVE_OFFSET_PX = 140;

/**
 * Documentation shell: a scroll-spy sidebar beside a scrolling <main> that
 * projects the page's <section id="…"> blocks.
 *
 * The sidebar has two parts:
 *   • `flatGroups` — plain links whose sections are always in the DOM; the anchor
 *     href scrolls to them.
 *   • `panels` — accordion sections (User Guide / User Architecture) listing every
 *     section. Only the active panel's sections are in the DOM, so a leaf click
 *     emits {@link navigate} to let the host switch the view before scrolling.
 */
@Component({
  selector: 'app-docs-shell',
  standalone: true,
  imports: [],
  templateUrl: './docs-shell.component.html',
  styleUrl: './docs-shell.component.css',
})
export class DocsShellComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Always-visible plain links. */
  @Input() flatGroups: NavGroup[] = [];
  /** Collapsible accordion sections. */
  @Input() panels: NavPanel[] = [];
  /** Optional heading shown above the accordion panels. */
  @Input() panelsHeading = '';
  /** Route this shell is mounted at — used for the in-page anchor links. */
  @Input() basePath = '/docs';
  /** Sidebar title and optional subtitle. */
  @Input() heading = 'Clinical AI Studio';
  @Input() subheading = '';

  /** Which panel's sections are currently rendered in <main> — drives scroll-spy and auto-expands it. */
  @Input()
  set activePanel(value: string | null) {
    this._activePanel = value;
    if (value) this.expanded.add(value);
  }
  get activePanel(): string | null {
    return this._activePanel;
  }
  private _activePanel: string | null = null;

  /** Emitted when an accordion leaf is clicked; the host switches the view, then the section renders. */
  @Output() readonly navigate = new EventEmitter<{ panel: string; id: string }>();

  @ViewChild('scrollRoot') private scrollRoot?: ElementRef<HTMLElement>;
  @ViewChild('sideNav') private sideNav?: ElementRef<HTMLElement>;

  /** Section currently under the reading line — drives the sidebar highlight. */
  readonly activeId = signal<string>('');

  /** Expanded accordion panels (all start open so the full tree is visible). */
  readonly expanded = new Set<string>();

  private frame: number | null = null;

  /** All sidebar section ids that could be in the DOM right now: flat links + the active panel's. */
  private get ids(): string[] {
    const flat = this.flatGroups.flatMap((g) => g.items.map((i) => i.id));
    const panel = this.panels.find((p) => p.id === this._activePanel);
    const panelIds = panel ? panel.groups.flatMap((g) => g.items.map((i) => i.id)) : [];
    return [...flat, ...panelIds];
  }

  private readonly onScroll = (): void => {
    // Coalesce bursts of scroll events into one measurement per frame.
    if (this.frame !== null) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.updateActive();
    });
  };

  ngOnInit(): void {
    this.panels.forEach((p) => this.expanded.add(p.id));
    const ids = this.ids;
    if (ids.length) this.activeId.set(ids[0]);
  }

  /**
   * Map the sidebar to a section the host just navigated to: expand its panel,
   * highlight it, and scroll the sidebar to it. Called after the view switches so
   * the nav reflects the page immediately, without waiting for the smooth scroll.
   */
  focusSection(id: string, panelId?: string | null): void {
    if (panelId) this.expanded.add(panelId);
    this.activeId.set(id);
    // Reveal once the (possibly just-expanded) list has rendered.
    setTimeout(() => this.revealActiveLink(id), 0);
  }

  ngAfterViewInit(): void {
    const root = this.scrollRoot?.nativeElement;
    if (!root) return;
    root.addEventListener('scroll', this.onScroll, { passive: true });
    // Honour a deep link (/docs#spec) and set the initial highlight.
    setTimeout(() => this.updateActive(), 0);
  }

  ngOnDestroy(): void {
    this.scrollRoot?.nativeElement.removeEventListener('scroll', this.onScroll);
    if (this.frame !== null) cancelAnimationFrame(this.frame);
  }

  /** Last rendered section whose top has passed the reading line; falls back to the first. */
  private updateActive(): void {
    const root = this.scrollRoot?.nativeElement;
    if (!root) return;
    const rootTop = root.getBoundingClientRect().top;

    // Only consider sections that are actually in the DOM right now.
    const rendered = this.ids
      .map((id) => ({ id, el: document.getElementById(id) }))
      .filter((x): x is { id: string; el: HTMLElement } => x.el !== null);
    if (!rendered.length) return;

    let current = rendered[0].id;
    for (const { id, el } of rendered) {
      if (el.getBoundingClientRect().top - rootTop <= ACTIVE_OFFSET_PX) current = id;
      else break;
    }

    // Bottom of the page: the final section can never reach the reading line.
    if (root.scrollTop + root.clientHeight >= root.scrollHeight - 4) {
      current = rendered[rendered.length - 1].id;
    }

    if (current !== this.activeId()) {
      this.activeId.set(current);
      this.revealActiveLink(current);
    }
  }

  /** Keep the highlighted link visible inside the (independently scrolling) sidebar. */
  private revealActiveLink(id: string): void {
    const nav = this.sideNav?.nativeElement;
    if (!nav) return;
    const link = nav.querySelector<HTMLElement>(`[data-nav-id="${id}"]`);
    link?.scrollIntoView({ block: 'nearest' });
  }

  isExpanded(id: string): boolean {
    return this.expanded.has(id);
  }

  togglePanel(id: string): void {
    if (this.expanded.has(id)) this.expanded.delete(id);
    else this.expanded.add(id);
  }

  /** Flat link: the section is always in the DOM, so the href scrolls — just pre-highlight. */
  selectFlat(id: string): void {
    this.activeId.set(id);
    this.revealActiveLink(id);
  }

  /** Accordion leaf: target may not be rendered yet, so ask the host to switch the view. */
  selectLeaf(panel: string, id: string, event: Event): void {
    event.preventDefault();
    this.activeId.set(id);
    this.revealActiveLink(id);
    this.navigate.emit({ panel, id });
  }

  linkClass(id: string): string {
    const base =
      'block border-l-2 pl-3 pr-3 py-2 rounded-r-lg text-sm transition-all duration-200';
    return id === this.activeId()
      ? `${base} border-[#1594fd] bg-blue-50 text-[#1594fd] font-semibold`
      : `${base} border-transparent text-black font-medium hover:bg-gray-100 hover:translate-x-1`;
  }
}
